import { Flex, Image, ConnectionItem, Text } from "@wayflyer/flyui";
import { useMemo, useState, type ReactNode } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import { connectByPath } from "../../data-sources";
import type { ConnectionPropertiesSchema, ConnectorPropertiesSchema } from "../../data-sources/schemas";

import { reconnectPostPath, reconnectStartPath, useConnectionFlowsConfig } from "../config";

import { COMMON_MESSAGES } from "../common-messages";

const connectionBadge = (
  connection: ConnectionPropertiesSchema,
  intl: IntlShape,
  supportsReconnection: boolean
): { badgeStatus: "positive" | "critical"; badgeText: string } =>
  connection.isConnected || !supportsReconnection
    ? { badgeStatus: "positive", badgeText: intl.formatMessage(COMMON_MESSAGES.connected) }
    : { badgeStatus: "critical", badgeText: intl.formatMessage(COMMON_MESSAGES.notConnected) };

export const ConnectionsList = ({
  connectorProperties,
  connectForm,
  renderReconnectForm,
  renderConnectAnother,
  renderConnectAnotherHelp,
  renderConnectionLogo,
}: {
  connectorProperties: ConnectorPropertiesSchema;
  connectForm: ReactNode;
  renderReconnectForm?: (connection: ConnectionPropertiesSchema) => ReactNode;
  /** The clickable "connect another" CTA. Hidden once the connect form is open. */
  renderConnectAnother?: (onClick: () => void) => ReactNode;
  /** Static help copy shown alongside the CTA. Always rendered, regardless of form visibility. */
  renderConnectAnotherHelp?: () => ReactNode;
  /** Per-connection logo override, for connectors where each connection can be a different platform. Defaults to `connectorProperties.connectionLogoSrc`. */
  renderConnectionLogo?: (connection: ConnectionPropertiesSchema) => ReactNode;
}) => {
  const intl = useIntl();
  const [isFormVisible, setFormVisible] = useState(false);
  const { supportsReconnection, defaultCallbackPath } = useConnectionFlowsConfig();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const connections = useMemo(() => connectorProperties?.connections ?? [], [connectorProperties]);
  const supportsFormReconnect = supportsReconnection && !!renderReconnectForm;

  const [expandedConnectionIds, setExpandedConnectionIds] = useState<ReadonlySet<number>>(new Set());

  const toggleReconnectForm = (connectionId: number) =>
    setExpandedConnectionIds((expanded) => {
      const next = new Set(expanded);
      if (next.has(connectionId)) {
        next.delete(connectionId);
      } else {
        next.add(connectionId);
      }
      return next;
    });

  return (
    <Flex direction="column" width="100%" gap="6">
      {connections.length > 0 ? (
        <>
          <Text size="sm" fontWeight="semiBold">
            {intl.formatMessage(COMMON_MESSAGES.connections)}
          </Text>

          <Flex direction="column" gap="4">
            <Flex direction="column" gap="2">
              {connections.map((connection) => {
                // `reconnectPath` stays server-computed only for shopify2 (its reconnect route needs
                // the shop domain, which only the server can read from a KMS-decrypted secret); every
                // other connector's reconnect path is built here from its slug.
                const connectionReconnectPath =
                  connection.reconnectPath ??
                  reconnectStartPath(customerDomain, connectorProperties.slug, connection.id, defaultCallbackPath);
                const connectionReconnectPostPath = reconnectPostPath(
                  customerDomain,
                  connectorProperties.slug,
                  connection.id,
                  defaultCallbackPath
                );
                const reconnectsByForm =
                  supportsReconnection &&
                  !connection.isConnected &&
                  supportsFormReconnect &&
                  !!connectionReconnectPostPath;
                const reconnectsByRedirect =
                  supportsReconnection && !connection.isConnected && !reconnectsByForm && !!connectionReconnectPath;
                return (
                  <Flex key={connection.id} direction="column" gap="2">
                    <ConnectionItem
                      label={connection.identifierName ?? connectorProperties.name}
                      logo={
                        renderConnectionLogo ? (
                          renderConnectionLogo(connection)
                        ) : connectorProperties.connectionLogoSrc ? (
                          <Image src={connectorProperties.connectionLogoSrc} alt="" />
                        ) : undefined
                      }
                      {...connectionBadge(connection, intl, supportsReconnection)}
                      {...((reconnectsByForm || reconnectsByRedirect) && {
                        actions: [
                          {
                            content: intl.formatMessage(COMMON_MESSAGES.reconnectAction),
                            variant: "Tertiary",
                            onAction: () => {
                              if (reconnectsByForm) {
                                toggleReconnectForm(connection.id);
                              } else {
                                connectByPath(connectionReconnectPath as string);
                              }
                            },
                          },
                        ],
                      })}
                    />
                    {reconnectsByForm && expandedConnectionIds.has(connection.id) && (
                      <Flex direction="column" padding={["4", "0"]} gap="2">
                        {renderReconnectForm?.(connection)}
                      </Flex>
                    )}
                  </Flex>
                );
              })}
            </Flex>

            {!isFormVisible && renderConnectAnother?.(() => setFormVisible(true))}

            {renderConnectAnotherHelp && <Flex margin={["8", "0", "0"]}>{renderConnectAnotherHelp()}</Flex>}
          </Flex>

          {isFormVisible && (
            <Flex direction="column" padding={["4", "0"]} gap="2">
              {connectForm}
            </Flex>
          )}
        </>
      ) : (
        connectForm
      )}
    </Flex>
  );
};
